import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  billingApi,
  FilterInvoiceDto,
  FilterClaimsDto,
  CreateInvoiceDto,
  UpdateInvoiceDto,
  ProcessPaymentDto,
} from '../services/api/billingApi';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/utils/errorHandling';

// Query keys factory
export const billingKeys = {
  all: ['billing'] as const,
  lists: () => [...billingKeys.all, 'list'] as const,
  list: (filters: FilterInvoiceDto) => [...billingKeys.lists(), filters] as const,
  details: () => [...billingKeys.all, 'detail'] as const,
  detail: (id: string) => [...billingKeys.details(), id] as const,
  statistics: () => [...billingKeys.all, 'statistics'] as const,
};

// Get all invoices
/**
 * `enabled` matters more than it looks: an omitted filter fetches EVERY
 * invoice in the clinic, so a caller that only wants one patient's bills
 * must not fall back to `{}` when it decides not to ask. Defaults to
 * true, so existing callers are unchanged.
 */
export const useInvoices = (
  filters: FilterInvoiceDto = {},
  enabled = true,
) => {
  return useQuery({
    queryKey: billingKeys.list(filters),
    queryFn: () => billingApi.getInvoices(filters),
    staleTime: 30000, // 30 seconds
    enabled,
  });
};

// Get insurance claims (filtered + paginated + summarised server-side)
export const useClaims = (filters: FilterClaimsDto = {}) => {
  return useQuery({
    queryKey: [...billingKeys.all, 'claims', filters] as const,
    queryFn: () => billingApi.getClaims(filters),
    staleTime: 30000,
    // Keep the previous page/filter visible while the next loads.
    placeholderData: keepPreviousData,
  });
};

// Get single invoice
export const useInvoice = (id: string) => {
  return useQuery({
    queryKey: billingKeys.detail(id),
    queryFn: () => billingApi.getInvoiceById(id),
    enabled: !!id,
  });
};

// Get billing statistics
export const useBillingStatistics = () => {
  return useQuery({
    queryKey: billingKeys.statistics(),
    queryFn: () => billingApi.getStatistics(),
    staleTime: 60000, // 1 minute
  });
};

// Create invoice
export const useCreateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInvoiceDto) => billingApi.createInvoice(data),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: billingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.statistics() });
      toast.success('Invoice created successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to create invoice');
    },
  });
};

// Update invoice
export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInvoiceDto }) =>
      billingApi.updateInvoice(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific invoice and lists
      queryClient.invalidateQueries({ queryKey: billingKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.statistics() });
      toast.success('Invoice updated successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to update invoice');
    },
  });
};

// Process payment
export const useProcessPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProcessPaymentDto }) =>
      billingApi.processPayment(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific invoice and lists
      queryClient.invalidateQueries({ queryKey: billingKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: billingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.statistics() });
      toast.success('Payment processed successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to process payment');
    },
  });
};

// Delete invoice
export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billingApi.deleteInvoice(id),
    onSuccess: () => {
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: billingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.statistics() });
      toast.success('Invoice deleted successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error) || 'Failed to delete invoice');
    },
  });
};
